import { FileText, UserRound } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SenderAvatar } from '@/features/communications/message-row'
import { formatMessageTime } from '@/features/communications/message-format'
import { formatFileSize } from '@/features/communications/formatting'
import type { QuoteComment } from '../publishing/api'
import { commentFileUrl, type CommentFile } from './api'

function FileLink({ file, className, children }: { file: CommentFile; className: string; children: React.ReactNode }) {
  return (
    // eslint-disable-next-line no-restricted-syntax -- a signed API file, opened in a new tab
    <a className={className} href={commentFileUrl(file)} rel="noreferrer" target="_blank">
      {children}
    </a>
  )
}

/** Photos as thumbnails and other files by name; each opens in a new tab (as in storefront chat). */
function CommentFiles({ files, customer }: { files: CommentFile[]; customer: boolean }) {
  if (files.length === 0) return null
  const photos = files.filter((file) => file.kind === 'image')
  const documents = files.filter((file) => file.kind !== 'image')
  return (
    <div className={cn('flex w-full flex-col gap-1.5', customer ? 'items-start' : 'items-end')}>
      {photos.length > 0 && (
        <ul aria-label="Photos" className={cn('flex flex-wrap gap-2', !customer && 'justify-end')}>
          {photos.map((photo) => (
            <li key={photo.id}>
              <FileLink
                className="block overflow-hidden rounded-lg border bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                file={photo}
              >
                <img
                  alt={photo.filename}
                  className="h-28 w-28 object-cover sm:h-32 sm:w-32"
                  loading="lazy"
                  src={commentFileUrl(photo)}
                />
              </FileLink>
            </li>
          ))}
        </ul>
      )}
      {documents.length > 0 && (
        <ul aria-label="Files" className="w-full max-w-sm space-y-1">
          {documents.map((file) => (
            <li key={file.id}>
              <FileLink
                className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                file={file}
              >
                <FileText aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">{file.filename}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{formatFileSize(file.size_bytes)}</span>
              </FileLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** One comment as a bubble: the customer on the left, Sales on the right, the time underneath and the name on the avatar. */
export function CommentMessage({ comment }: { comment: QuoteComment }) {
  const customer = comment.actor_id === null
  return (
    <article
      aria-label={comment.author_name}
      className={cn('flex items-start gap-2 px-3 py-1.5 sm:px-4', !customer && 'flex-row-reverse')}
      data-testid={`quote-comment-${comment.id}`}
    >
      <span title={comment.author_name}>
        {customer ? (
          <SenderAvatar name={comment.author_name} tone="counterpart" />
        ) : (
          <SenderAvatar icon={UserRound} tone="ours" />
        )}
      </span>
      <div className={cn('flex min-w-0 max-w-[85%] flex-col gap-1.5 sm:max-w-[75%]', customer ? 'items-start' : 'items-end')}>
        {comment.body ? (
          <div
            className={cn(
              'min-w-0 max-w-full rounded-2xl px-3.5 py-2 text-foreground',
              customer ? 'rounded-bl-md bg-muted' : 'rounded-br-md bg-usa-blue/10 dark:bg-usa-blue/25',
            )}
          >
            <p className="whitespace-pre-wrap break-words text-sm">{comment.body}</p>
          </div>
        ) : null}
        <CommentFiles customer={customer} files={comment.files} />
        <time className="px-1 text-[11px] tabular-nums text-muted-foreground" dateTime={comment.created_at}>
          {formatMessageTime(comment.created_at)}
        </time>
      </div>
    </article>
  )
}
